// Stair connections belong to the building, not to a floor snapshot. Geometry
// is expressed in shared plan coordinates and heights relative to each level.
const RAD = Math.PI / 180;
export function stairLevels(S) {
  return S._stairLevels || [{ id: S.activeFloorId || 'ground', name: S.floorName || 'Ground floor', buildingId: S.buildingId || 'building-main', elevation: S.elevation || 0 },
    ...(S.floors || []).map(f => ({ id: f.id, name: f.name, buildingId: f.buildingId || f.plan?.buildingId || 'building-main', elevation: f.elevation || 0 }))];
}
export function withStairContext(S, level) {
  if (!(S.stairs || []).length) return level.plan;
  return { ...level.plan, activeFloorId: level.id, floorName: level.name, elevation: level.elevation,
    stairs: S.stairs, _stairLevels: stairLevels(S) };
}
export function stairSpec(S, stair) {
  const levels = stairLevels(S), lower = levels.find(f => f.id === stair.fromFloorId), upper = levels.find(f => f.id === stair.toFloorId);
  const rise = upper && lower ? upper.elevation - lower.elevation : 0;
  if (!lower || !upper || rise < 0.1 || rise > 36) return null;
  const count = Math.ceil(rise / 0.18);
  return { stair, lower, upper, rise, count, riser: rise / count, tread: stair.run / count };
}
export const visibleStairs = S => (S.stairs || []).filter(s => { const c = stairSpec(S, s); return c && (S.elevation || 0) >= c.lower.elevation && (S.elevation || 0) <= c.upper.elevation; });
export const connectedStairs = S => (S.stairs || []).filter(s => (S._groundFloorIds || [S.activeFloorId || 'ground']).some(id => s.fromFloorId === id || s.toFloorId === id));
export const stairWorld = (s, u, v = 0) => ({ x: s.x + Math.cos(s.pan * RAD) * u - Math.sin(s.pan * RAD) * v, y: s.y + Math.sin(s.pan * RAD) * u + Math.cos(s.pan * RAD) * v });
export const stairLocal = (s, x, y) => ({ u: (x - s.x) * Math.cos(s.pan * RAD) + (y - s.y) * Math.sin(s.pan * RAD), v: -(x - s.x) * Math.sin(s.pan * RAD) + (y - s.y) * Math.cos(s.pan * RAD) });
export function stairFootprint(s, landings = true, margin = 0) {
  const half = s.run / 2 + (landings ? s.landing : 0) + margin, w = s.width / 2 + margin;
  return [[-half, -w], [half, -w], [half, w], [-half, w]].map(([u, v]) => stairWorld(s, u, v));
}
export function stairLanding(s, upper) { return stairWorld(s, (upper ? 1 : -1) * (s.run / 2 + s.landing / 2)); }
export function nearStairLanding(stair, upper, position) {
  if (!position) return false;
  const p = stairLocal(stair, position.x, position.z);
  return Math.abs(p.u - (upper ? 1 : -1) * (stair.run / 2 + stair.landing / 2)) <= stair.landing / 2 + 0.35 && Math.abs(p.v) <= stair.width / 2 + 0.35;
}
export function onStairFootprint(s, x, y, landings = true, margin = 0) {
  const { u, v } = stairLocal(s, x, y);
  return Math.abs(u) < s.run / 2 + (landings ? s.landing : 0) + margin && Math.abs(v) < s.width / 2 + margin;
}
// A cut is a physical opening, distinct from a coverage-only exclusion. Cut
// through any intervening floor/ceiling; keep both endpoint landings intact.
export function stairHoles(S, elevation = S.elevation || 0) {
  return (S.stairs || []).filter(s => { const c = stairSpec(S, s); return c && elevation > c.lower.elevation + 0.001 && elevation <= c.upper.elevation + 0.001; })
    .map(s => stairFootprint(s, false, 0.08));
}
export function inStairHole(S, x, y) {
  return (S.stairs || []).some(s => { const c = stairSpec(S, s); return c && (S.elevation || 0) > c.lower.elevation + 0.001 && (S.elevation || 0) <= c.upper.elevation + 0.001 && onStairFootprint(s, x, y, false, 0.08); });
}
export function defaultStair(S, point) {
  const levels = stairLevels(S).filter(f => f.buildingId === (S.buildingId || 'building-main')).sort((a, b) => a.elevation - b.elevation), current = levels.find(f => f.id === S.activeFloorId) || levels[0];
  const above = levels.find(f => f.elevation > current.elevation + 0.1), below = levels.filter(f => f.elevation < current.elevation - 0.1).at(-1);
  if (!above && !below) return null;
  const lower = above ? current : below, upper = above || current, rise = upper.elevation - lower.elevation;
  if (rise > 36) return null;
  return { name: 'Stairs ' + ((S.stairs || []).length + 1), x: point.x, y: point.y, pan: 0, width: 1.2,
    run: Math.round(Math.ceil(rise / 0.18) * 28) / 100, landing: 1.2, fromFloorId: lower.id, toFloorId: upper.id };
}
export function stairObstructions(S) {
  const out = [], elevation = S.elevation || 0;
  for (const s of S.stairs || []) {
    const c = stairSpec(S, s); if (!c || elevation < c.lower.elevation || elevation > c.upper.elevation) continue;
    for (const upper of [false, true]) {
      const u = (upper ? 1 : -1) * (s.run / 2 + s.landing / 2), a = stairWorld(s, u, -s.width / 2), b = stairWorld(s, u, s.width / 2), h = (upper ? c.upper.elevation : c.lower.elevation) - elevation;
      out.push({ id: `${s.id}:landing-${upper ? 'upper' : 'lower'}`, sourceId: s.id, objectKind: 'stair', name: s.name, kind: 'solid', mountRule: 'prohibited',
        x1: a.x, y1: a.y, x2: b.x, y2: b.y, bottom: h - 0.16, h, thickness: s.landing });
    }
    for (let i = 0; i < c.count; i++) {
      const a = stairWorld(s, -s.run / 2 + (i + 0.5) * c.tread, -s.width / 2), b = stairWorld(s, -s.run / 2 + (i + 0.5) * c.tread, s.width / 2);
      out.push({ id: `${s.id}:step-${i}`, sourceId: s.id, objectKind: 'stair', name: s.name, kind: 'solid', mountRule: 'prohibited',
        x1: a.x, y1: a.y, x2: b.x, y2: b.y, bottom: c.lower.elevation - elevation, h: c.lower.elevation - elevation + (i + 1) * c.riser, thickness: c.tread });
    }
  }
  return out;
}
